// src/features/uploadSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
const baseUrl = process.env.REACT_APP_BASE_URL;

export interface FileInfo {
  fileName: string;
  fileType: string;
  fileSize: number;
  file: File;
  language: string;
  date: string;
  meetingType: any;
  meetingTitle: any;
  meetingTypeName: any;
}

export interface ApiInfo {
  s3Key: string;
  task_id: string;
}

export interface SubOption {
  id: number;
  name: string;
}

export interface MeetingType {
  id: number;
  name: string;
  sub_options: SubOption[];
}

interface UploadState {
  file: FileInfo | null;
  apiInfo: ApiInfo | null;
  result: ResultInfo | null;
  meetingTypes: MeetingType[];
  currentDateIST: string | null;
  loading: boolean;
  error: string | null;
  MeetingTypeFetched: boolean;
  meetingAttendees: any | null;
  isDirty: boolean;
  showDedupErrorModal: boolean;
  dedupErrorMessage: string;
  dedupErrorModalType: string;
}

const initialState: UploadState = {
  file: null,
  apiInfo: null,
  result: null,
  meetingTypes: [],
  currentDateIST: null,
  loading: false,
  error: null,
  MeetingTypeFetched: false,
  meetingAttendees: null,
  isDirty: false,
  showDedupErrorModal: false,
  dedupErrorMessage: "",
  dedupErrorModalType: "DUPLICATE",
};

export interface ResultInfo {
  original_transcript: string;
  translated_transcript_en: string;
  summary_en: string;
  transcription_readiness_score: null;
}

export const languages = [
  { id: 1, code: 'en', label: 'English' },
  { id: 2, code: 'hi', label: 'Hindi' },
  { id: 3, code: 'mr', label: 'Marathi' },
  { id: 4, code: 'ta', label: 'Tamil' },
  { id: 5, code: 'te', label: 'Telugu' },
  { id: 6, code: 'kn', label: 'Kannada' },
  { id: 7, code: 'gu', label: 'Gujarati' },
  { id: 8, code: 'bn', label: 'Bengali' },
  { id: 9, code: 'ml', label: 'Malayalam' },
  { id: 10, code: 'or', label: 'Odia' },
  { id: 11, code: 'ur', label: 'Urdu' },
  { id: 12, code: 'pa', label: 'Punjabi' },
  { id: 13, code: 'sa', label: 'Sanskrit' },
  { id: 14, code: 'ks', label: 'Kashmiri' },
  { id: 15, code: 'as', label: 'Assamese' },
  { id: 16, code: 'gom', label: 'Konkani' },
  { id: 17, code: 'doi', label: 'Dogri' },
  { id: 18, code: 'brx', label: 'Bodo' },
  { id: 19, code: 'mai', label: 'Maithili' },
  { id: 20, code: 'mni', label: 'Manipuri' },
  { id: 21, code: 'ne', label: 'Nepali' },
  { id: 22, code: 'sat', label: 'Santhali' },
  { id: 23, code: 'sd', label: 'Sindhi' },
];

export const regionalLanguages = [
  { code: 'hi', label: 'Hindi' },
  { code: 'mr', label: 'Marathi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'kn', label: 'Kannada' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'bn', label: 'Bengali' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'or', label: 'Odia' },
  { code: 'ur', label: 'Urdu' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'sa', label: 'Sanskrit' },
  { code: 'ks', label: 'Kashmiri' },
  { code: 'as', label: 'Assamese' },
  { code: 'gom', label: 'Konkani' },
  { code: 'doi', label: 'Dogri' },
  { code: 'brx', label: 'Bodo' },
  { code: 'mai', label: 'Maithili' },
  { code: 'mni', label: 'Manipuri' },
  { code: 'ne', label: 'Nepali' },
  { code: 'sat', label: 'Santhali' },
  { code: 'sd', label: 'Sindhi' },
];

export const fetchMeetingTypes = createAsyncThunk(
  'upload/fetchMeetingTypes',
  async () => {
    const response = await axios.get(`${baseUrl}api/master/meeting-types`);
    return response.data;
  }
);

const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    setFileData(state, action: PayloadAction<FileInfo>) {
      state.file = action.payload;
    },
    setApiInfo(state, action: PayloadAction<ApiInfo>) {
      state.apiInfo = action.payload;
    },
    setResult(state, action: PayloadAction<ResultInfo>) {
      state.result = action.payload;
    },
    setMeetingAttendees(state, action: PayloadAction<any>) {
      state.meetingAttendees = action.payload;
    },
    setIsDirty(state, action: PayloadAction<boolean>) {
      state.isDirty = action.payload;
    },
    resetUploadState(state) {
      return {
        ...initialState,
      };
    },
    setDedupErrorMessage: (state, action: PayloadAction<string>) => {
      state.dedupErrorMessage = action.payload;
      state.showDedupErrorModal = true;
    },
    setDedupErrorModalType: (state, action: PayloadAction<string>) => {
      state.dedupErrorModalType = action.payload;
    },
    openDedupErrorModal: (state) => {
      state.showDedupErrorModal = true;
    },
    closeDedupErrorModal: (state) => {
      state.showDedupErrorModal = false;
      state.dedupErrorMessage = "";
      state.dedupErrorModalType = "DUPLICATE";
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeetingTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetingTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.meetingTypes = action.payload.meeting_types;
        state.currentDateIST = action.payload.current_date_ist;
        state.MeetingTypeFetched = true;
      })
      .addCase(fetchMeetingTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch meeting types";
        state.MeetingTypeFetched = true;
      });
  },
});

export const {
  setFileData,
  setApiInfo,
  setResult,
  setMeetingAttendees,
  setIsDirty,
  resetUploadState,
  setDedupErrorMessage,
  setDedupErrorModalType,
  openDedupErrorModal,
  closeDedupErrorModal
} = uploadSlice.actions;

export default uploadSlice.reducer;